import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useTransition, animated, config } from "react-spring";
import classNames from "classnames";
import type { MapOptions, LngLatBoundsLike } from "mapbox-gl";
import type LivingMap from "@livingmap/core-mapping";

import {
  Map,
  Compass,
  Header,
  CentreControl,
  FloorSelector,
  PanControl,
  SearchControl,
  Keyboard,
  ZoomControl,
  SearchResults,
} from "../../components";
import { useSession, useImagePreloader } from "../../hooks";
import {
  ConfigurationResponse,
  InteractionEventTypes,
  Feature,
  useLazyPostRouteQuery,
  SearchTag,
  SearchTagsCollection,
  SearchTagWithSource,
  useLazyGetFeaturesBySearchQuery,
  useLazyGetHealthQuery,
} from "../../redux/services/config";
import { PLUGIN_IDS } from "../../components/Map/plugins/types";
import FloorControl from "../../components/Map/plugins/floor-control";
import PositionPlugin from "../../components/Map/plugins/position-control";
import ClusteredPinPlugin from "../../components/Map/plugins/clustered-pin-control";
import InteractionPlugin from "../../components/Map/plugins/interaction-control";
import {
  createLMFeatures,
  getAnalyticsFeatureName,
  getBoundingBox,
  throttle,
} from "../../utils";
import styles from "./BaseWithHeader.module.scss";
import IFrame from "../../components/IFrame/IFrame";
import ShareToMobile from "../../components/ShareToMobile/ShareToMobile";
import MoveDownView from "../../components/MoveDownView/MoveDownView";
import { useDispatch } from "react-redux";
import {
  setMoveDownPopups,
  setIsStepFreeRoute,
  setControlsAlwaysLow,
} from "../../redux/slices/applicationSlice";
import { useAppSelector } from "../../redux/hooks";
import { useOnlineMode } from "../../hooks/useOnlineMode";
import { Feature as GeoJsonFeature } from "geojson";
import RoutingPlugin from "../../components/Map/plugins/routing-control";
import { useStepFreeRouting } from "../../hooks/useStepFreeRouting";
import { useRouteResponse } from "../../hooks/useRouteResponse";
import { useDisplayRoute } from "../../hooks/useDisplayRoute";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import {
  ControlSizeScale,
  LocationStatus,
  UISize,
  UITheme,
} from "../../components/types";
import StepFreeChip from "../../components/StepFreeChip/StepFreeChip";
import POICarousel from "../../components/Map/components/POICarousel/POICarousel";
import {
  FeatureWithDistance,
  addDistanceToFeatures,
} from "../../utils/addDistanceToFeatures";
import { parseLanguageObject } from "../../utils/parseLanguageObject";
import SearchTags from "../../components/SearchTags/SearchTags";
import { MIN_CHARS, useSearchResults } from "../../hooks/useSearchResults";
import { is4kScreen } from "../../utils/is4kScreen";
import { useLazyGetTransportFeedsQuery } from "../../redux/services/transportAPI";
import SheetContainer from "../../containers/SheetContainer/SheetContainer";
import Footer from "../../components/Footer/Footer";
import MessagingProtocol from "../../components/MessagingProtocol/MessagingProtocol";
import ControlsWrapper from "../../components/ControlsWrapper/ControlsWrapper";
import { useComponentHeight } from "../../hooks/useComponentHeight";
import { useZoomLimit } from "../../hooks/useZoomLimit";
import { UIProvider } from "../../contexts/UIContext";
import { MapControlLocationButton } from "@livingmap/hippo";
import { useQueryParams } from "hooks/useQueryParams";
import { useTranslation } from "react-i18next";
import LanguageSelect from "components/LanguageSelect/LanguageSelect";
import i18n from "i18n";
import { FloatingButton } from "@livingmap/hippo";
import { getScreenId } from "../../utils/getScreenId";
import SearchResultsCarousel from "../../components/Map/components/SearchResultsCarousel/SearchResultsCarousel";
import LanguageChangeErrorModal from "components/LanguageChangeErrorModal/LanguageChangeErrorModal";
import { sortFeaturesByDistance } from "utils/sortFeaturesByDistance";
import { useHandleFeatureRouteLoad } from "hooks/useHandleFeatureRouteLoad";
import { useHandleQsSearchTagLoad } from "hooks/useHandleQsSearchTagLoad";
import { buildKioskShortlink } from "utils/buildKioskShortlink";

const DEFAULT_FEATURE_HORIZON_HEIGHT = 715;

export interface OnTouchHandlerOptions {
  featureID?: number;
  lm_id?: string;
  featureName?: string;
  searchTagID?: number;
  title?: string;
  language?: string;
  query?: string;
  stepFree?: boolean;
}

interface Props {
  data: ConfigurationResponse;
  preloadedFeatures: Feature[];
  searchTags?: SearchTagsCollection;
  uptime: string | null;
}

enum StepFreeStatus {
  SHOW = "show",
  HIDE = "hide",
  INACTIVE = "inactive",
}

type PreviousSearchContextSource = "keyboard" | "quick_search";

interface PreviousSearchContext {
  searchTerm: string;
  features: Feature[];
  source: PreviousSearchContextSource;
}

const BaseWithHeader: React.FC<Props> = ({
  data,
  preloadedFeatures,
  searchTags,
  uptime,
}) => {
  const { isRoutingMode, isDebugMode } = useQueryParams();

  // config data
  const {
    components: { map_primary, iframe, top_bar },
    languages,
    stylesheet,
    location,
    server,
    floors,
    map_key: mapboxApiKey,
    screen_id,
    display,
    title,
    routing,
  } = data;

  const { controls: primaryControls, search } = map_primary;
  const activeLanguage =
    languages.available.find((lang) => lang.id === i18n.language) ||
    languages.default;
  const isLatinLanguage = activeLanguage?.latin ?? true;
  const enableOfflineSearch = activeLanguage?.latin === true;
  const controlSize: UISize = primaryControls.theme.size;
  const controlTheme: UITheme = primaryControls.theme.mode;
  const mapControlMarginBottom = primaryControls.theme.margin_bottom;
  const mapControlsAlwaysLow = primaryControls.theme.always_low;

  const { enable_images, pre_load_images } = search;
  const fullHD = !is4kScreen();

  const orientation =
    display.resolution_x > display.resolution_y ? "landscape" : "portrait";

  const noticeTimeout =
    map_primary.interaction.session_timeout -
    map_primary.interaction.session_timeout_notice;

  const activeLocationStyle = useMemo(() => {
    return {
      colour: "#4B82FF", // $blue-tone-400
      borderColour: "#FFF", // $white-tone
      displayPulse: true,
    };
  }, []);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { moveDownPopups, onlineMode, isStepFreeRoute, controlsAlwaysLow } =
    useAppSelector((state) => state.application);

  // refs
  const mapInstance = useRef<LivingMap | null>(null);
  const floorControlInstance = useRef<FloorControl | null>(null);
  const positionControlInstance = useRef<PositionPlugin | null>(null);
  const clusteredPinControlInstance = useRef<ClusteredPinPlugin | null>(null);
  const routingControlInstance = useRef<RoutingPlugin | null>(null);
  const interactionControlInstance = useRef<InteractionPlugin | null>(null);

  // state
  const [mapIsReady, setMapIsReady] = useState(false);
  const [mapReadyVersion, setMapReadyVersion] = useState(0);
  const [mapStylesheet, setMapStylesheet] = useState(
    `${stylesheet}?lang=${i18n.language}`,
  );
  const isSessionResetting = useRef(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [userLocationStyle, setUserLocationStyle] =
    useState(activeLocationStyle);
  const [searchIsActive, setSearchIsActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputActive, setSearchInputActive] = useState(true);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false);
  const [stepFreeStatus, setStepFreeStatus] = useState<StepFreeStatus>(
    StepFreeStatus.INACTIVE,
  );

  const [selectedFeature, setSelectedFeature] = useState<GeoJsonFeature | null>(
    null,
  );
  const [featureSelectionVersion, setFeatureSelectionVersion] = useState(0);
  const [countdownTimeInSeconds, setCountdownTimeInSeconds] = useState(
    noticeTimeout / 1000,
  );
  const [locationBtnActive, setLocationBtnActive] = useState(true);
  const [carouselFeatures, setCarouselFeatures] = useState<
    FeatureWithDistance[]
  >([]);
  const [useSearchResultsCarousel, setUseSearchResultsCarousel] =
    useState(false);
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");
  const [triggerKeyboardSearch, keyboardSearchResult] =
    useLazyGetFeaturesBySearchQuery();
  const [showKeyboardResults, setShowKeyboardResults] = useState(false);
  const [keyboardSearchError, setKeyboardSearchError] = useState(false);

  const [triggerHealthCheck] = useLazyGetHealthQuery();
  const [isLanguageChangeErrorModalOpen, setIsLanguageChangeErrorModalOpen] =
    useState(false);

  // State to track previous search context for navigation back from POI carousel
  const [previousSearchContext, setPreviousSearchContext] =
    useState<PreviousSearchContext | null>(null);

  const kioskTitle = parseLanguageObject(title, i18n.language);

  const { isZoomAtLimit, resetZoomLimit, updateZoomLimit } = useZoomLimit(
    map_primary.zoom.init,
    map_primary.zoom.min,
    map_primary.zoom.max,
  );

  const {
    debouncedSearchTerm,
    results: searchResults,
    isFetching: isFetchingSearchResults,
    isTitleSearch,
    isOfflineSearchEnabled,
  } = useSearchResults({
    features: preloadedFeatures || [],
    searchTerm,
    onSearchInputActive: setSearchInputActive,
    offlineSearchEnabled: enableOfflineSearch,
  });

  const [routeRequestTrigger, routeResult] = useLazyPostRouteQuery();

  const [transportFeedsRequestTrigger, transportFeedsResult] =
    useLazyGetTransportFeedsQuery();

  const { displayedRoute, displayedStepFree } = useRouteResponse(
    routeResult,
    selectedFeature?.id,
    featureSelectionVersion,
  );

  const { renderRoute } = useDisplayRoute({
    segments: displayedRoute?.segments,
    sequenceOrder: displayedRoute?.routeMetadata[0]?.sequenceOrder,
    routingControl: routingControlInstance.current,
  });

  const transitions = useTransition(searchIsActive, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    reverse: searchIsActive,
    config: {
      ...config.molasses,
      duration: 200,
    },
  });

  const combinedSearchTags = useMemo<SearchTagWithSource[]>(() => {
    if (!searchTags) return [];

    const appendSource = (
      tags: SearchTag[],
      source: SearchTagWithSource["source"],
    ) => tags.map((tag) => ({ ...tag, source }));

    const { searchTags: defaultTags = [], bespokeSearchTags = [] } = searchTags;

    return [
      ...appendSource(defaultTags, "normal"),
      ...appendSource(bespokeSearchTags, "bespoke"),
    ];
  }, [searchTags]);

  const { checkOnlineMode } = useOnlineMode(() =>
    handleTouchEvent(
      InteractionEventTypes.OFFLINE_SESSION,
      !!map_primary?.interaction?.session_logging,
      false,
    ),
  );

  useImagePreloader(preloadedFeatures, pre_load_images);

  const handleUserLocationStyle = useCallback(
    (isActive = true) => {
      setUserLocationStyle(
        isActive
          ? activeLocationStyle
          : {
              colour: "#BDBDBD", // $grey-tone-400
              borderColour: "#757575", // $grey-tone-600
              displayPulse: false,
            },
      );
    },
    [activeLocationStyle],
  );

  const handleMapRecentre = useCallback(() => {
    mapInstance.current?.getMapboxMap().easeTo({
      center: map_primary.center,
      zoom: map_primary.zoom.init,
      bearing: map_primary.bearing,
      padding: 0,
    });

    floorControlInstance.current?.setActiveFloor(floors[map_primary.floor]);

    handleUserLocationStyle();

    resetZoomLimit();
  }, [
    floors,
    handleUserLocationStyle,
    map_primary.center,
    map_primary.bearing,
    map_primary.floor,
    map_primary.zoom.init,
    resetZoomLimit,
  ]);

  const handleSessionTimeout = async () => {
    isSessionResetting.current = true;
    setHasInteracted(false);
    setSelectedFeature(null);
    setSubmittedSearchTerm("");
    setPreviousSearchContext(null);
    setShowKeyboardResults(false);
    setUseSearchResultsCarousel(false);
    if (!controlsAlwaysLow) dispatch(setMoveDownPopups(false));
    dispatch(setIsStepFreeRoute(false));
    setStepFreeStatus(StepFreeStatus.INACTIVE);
    setCarouselFeatures([]);
    setIsLanguageChangeErrorModalOpen(false);

    clusteredPinControlInstance.current?.clearFeatureLabels();
    interactionControlInstance.current?.clearSelectedFeatureSource();
    routingControlInstance.current?.clear();

    handleMapRecentre();

    if (searchIsActive) {
      handleTouchEvent(
        InteractionEventTypes.SEARCH_DIALOG_CLOSE,
        !!map_primary?.interaction?.session_logging,
        false,
        {
          event_data: searchTerm,
        },
      );

      setSearchIsActive(false);
      setSearchTerm("");
    }

    if (isShareDialogOpen) setIsShareDialogOpen(false);
    if (isLanguageDialogOpen) setIsLanguageDialogOpen(false);
    // Only change the language if the health check is successful to prevent map tiles disappearing
    const response = await triggerHealthCheck();
    if (!response.isError && isSessionResetting.current) {
      i18n.changeLanguage(data.languages.default.id);
      document.documentElement.setAttribute(
        "dir",
        data.languages.default.orientation,
      );
    }
  };

  const { handleTouchEvent, isInactive, resetTimeouts } = useSession(
    map_primary.interaction.session_timeout,
    map_primary.interaction.session_timeout_notice,
    handleSessionTimeout,
    screen_id,
  );
  const { componentRef: footerRef, height: footerHeight } =
    useComponentHeight();

  const updateRouteLinePadding = useCallback(() => {
    const paddingAllowance = fullHD ? 50 : 100;

    const paddingX =
      (fullHD
        ? ControlSizeScale[controlSize]
        : ControlSizeScale[controlSize] * 2) + paddingAllowance;
    const paddingY =
      (fullHD
        ? ControlSizeScale[controlSize]
        : ControlSizeScale[controlSize] * 2) + paddingAllowance;

    routingControlInstance.current?.setRouteLinePadding({
      top: paddingY,
      bottom:
        footerHeight * 6 -
        (iframe ? iframe.height : 0) +
        paddingY +
        (moveDownPopups ? 0 : mapControlMarginBottom) +
        (iframe ? iframe.height : 0),
      left: paddingX,
      right: paddingX,
    });
  }, [
    controlSize,
    footerHeight,
    fullHD,
    iframe,
    mapControlMarginBottom,
    moveDownPopups,
  ]);

  const handleTransportFeedsRequest = useCallback(() => {
    // If the map is in offline mode or the selectedFeature doesn't have a transport_api property
    // we don't need to make a new request
    if (!onlineMode || !selectedFeature?.properties?.transport_api) return;

    transportFeedsRequestTrigger(selectedFeature.properties.transport_api);
  }, [onlineMode, selectedFeature, transportFeedsRequestTrigger]);

  const handleOnTouch = useCallback(
    (eventType: InteractionEventTypes, options?: OnTouchHandlerOptions) => {
      const isRouteEvent =
        eventType === InteractionEventTypes.ROUTE_REQUEST ||
        eventType === InteractionEventTypes.ROUTE_SHOWN ||
        eventType === InteractionEventTypes.ROUTE_FAIL;
      if (
        isSessionResetting.current &&
        (isRouteEvent || eventType === InteractionEventTypes.ASSET_DIALOG_OPEN)
      )
        return;
      if (!isRouteEvent) isSessionResetting.current = false;
      const extraData: { [key: string]: any } = {};

      if (eventType === InteractionEventTypes.ASSET_DIALOG_CLOSE) {
        return resetTimeouts();
      }

      if (
        eventType === InteractionEventTypes.SEARCH_QUERY_SEND &&
        options?.query
      ) {
        extraData.event_data = options.query;
      }

      if (
        eventType === InteractionEventTypes.SEARCH_RESULT_OPEN &&
        options?.featureName
      ) {
        extraData.event_data = options.featureName;
      }

      if (eventType === InteractionEventTypes.SEARCH_DIALOG_CLOSE) {
        extraData.event_data = searchTerm;
      }

      if (
        (eventType === InteractionEventTypes.ASSET_DIALOG_OPEN ||
          eventType === InteractionEventTypes.ROUTE_SHOWN ||
          eventType === InteractionEventTypes.ROUTE_FAIL ||
          eventType === InteractionEventTypes.ROUTE_REQUEST) &&
        options?.lm_id
      ) {
        const feature = preloadedFeatures.find(
          (feature) => feature.properties?.lm_id === options.lm_id,
        );

        const analyticsName = getAnalyticsFeatureName(languages, feature);

        extraData.event_data = {
          lm_id: options.lm_id,
          name: analyticsName,
        };

        if (
          eventType === InteractionEventTypes.ROUTE_SHOWN ||
          eventType === InteractionEventTypes.ROUTE_REQUEST ||
          eventType === InteractionEventTypes.ROUTE_FAIL
        )
          extraData.event_data = {
            ...extraData.event_data,
            step_free: options.stepFree ?? isStepFreeRoute,
          };
      }

      if (
        eventType === InteractionEventTypes.SHARE_TO_MOBILE_ROUTE_VIEW &&
        options?.featureID
      ) {
        extraData.event_data = `${options.featureID}`;
      }

      if (
        eventType === InteractionEventTypes.SEARCH_TAG_TOUCH &&
        options?.searchTagID &&
        options?.title
      ) {
        extraData.event_data = {
          id: options.searchTagID,
          title: options.title,
        };
      }

      if (
        eventType === InteractionEventTypes.LANGUAGE_CHANGE &&
        options?.language
      ) {
        extraData.event_data = options.language;
      }

      handleTouchEvent(
        eventType,
        !!map_primary?.interaction?.session_logging,
        !(
          eventType === InteractionEventTypes.LOWER_CONTROLS ||
          eventType === InteractionEventTypes.RAISE_CONTROLS ||
          eventType === InteractionEventTypes.ROUTE_SHOWN
        ),
        extraData,
      );

      if (eventType === InteractionEventTypes.RE_CENTER_MAP_TOUCH)
        handleMapRecentre();

      setHasInteracted(true);

      if (
        eventType === InteractionEventTypes.SEARCH_RESULT_OPEN ||
        eventType === InteractionEventTypes.PAN_CONTROL_TOUCH ||
        eventType === InteractionEventTypes.ZOOM_CONTROL_TOUCH ||
        eventType === InteractionEventTypes.LEVEL_SELECTOR_TOUCH ||
        eventType === InteractionEventTypes.ASSET_DIALOG_OPEN ||
        eventType === InteractionEventTypes.ROUTE_SHOWN ||
        eventType === InteractionEventTypes.SEARCH_TAG_TOUCH
      )
        setLocationBtnActive(false);

      if (eventType !== InteractionEventTypes.LEVEL_SELECTOR_TOUCH) return;

      !selectedFeature &&
        clusteredPinControlInstance.current?.reloadFeatureLabels();

      if (
        floorControlInstance.current?.getActiveFloor()?.id !==
        floors[map_primary.you_marker.floor].id
      ) {
        handleUserLocationStyle(false);
      } else {
        handleUserLocationStyle();
      }
    },
    [
      floors,
      handleMapRecentre,
      handleTouchEvent,
      handleUserLocationStyle,
      isStepFreeRoute,
      map_primary?.interaction?.session_logging,
      map_primary.you_marker.floor,
      preloadedFeatures,
      resetTimeouts,
      searchTerm,
      selectedFeature,
      languages,
    ],
  );

  const handleRouteRequest = useCallback(
    (stepFree: boolean) => {
      if (isSessionResetting.current || !selectedFeature) return;

      // If routing is disabled or the map is in offline mode, we just want to fit the map to the selected feature
      if (!isRoutingMode || !onlineMode) {
        const coords = (selectedFeature.geometry as GeoJSON.Point).coordinates;
        coords &&
          routingControlInstance.current?.handleFitMapToCoordinate(
            coords as LngLatBoundsLike,
          );
        return;
      }

      const { floor, latitude, longitude } =
        data.components.map_primary.you_marker;
      const floorPosition = Object.entries(data.floors).find(
        ([key]) => key === floor,
      );

      if (floorPosition) {
        routeRequestTrigger({
          from: {
            latitude,
            longitude,
            floorId: floorPosition[1].id,
          },
          to: {
            featureId: Number(selectedFeature.id),
          },
          project: data.project,
          ...(stepFree ? { options: { routeModifier: "step_free" } } : {}),
        });

        updateRouteLinePadding();
      }
      handleOnTouch(InteractionEventTypes.ROUTE_REQUEST, {
        lm_id: selectedFeature.properties?.lm_id,
        stepFree,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data.components.map_primary.you_marker,
      data.floors,
      data.project,
      onlineMode,
      routeRequestTrigger,
      isRoutingMode,
      selectedFeature,
      updateRouteLinePadding,
    ],
  );

  const handleKeyboardOnChange = useCallback(
    (value: string) => {
      resetTimeouts(); // Restart session timeouts
      setSearchTerm(value);
    },
    [resetTimeouts],
  );

  const handleKeyboardSearch = async (searchValue: string) => {
    if (!onlineMode) return;
    handleOnTouch(InteractionEventTypes.SEARCH_QUERY_SEND, {
      query: searchValue,
    });

    setSearchIsActive(false); // Hide the keyboard
    setCarouselFeatures([]);
    setSelectedFeature(null);
    setSubmittedSearchTerm(searchValue); // Ensure search bar is updated with query string
    setPreviousSearchContext(null); // Clear any previous context
    setKeyboardSearchError(false);

    const response = await triggerKeyboardSearch({
      id: getScreenId(),
      query: searchValue,
      lang: i18n.language,
    });

    if (response.error) {
      setKeyboardSearchError(true);
      setShowKeyboardResults(true);
      clusteredPinControlInstance.current?.clearFeatureLabels();
      return;
    }

    const features = response.data?.data ?? [];

    if (!features.length) {
      setShowKeyboardResults(true);
      clusteredPinControlInstance.current?.clearFeatureLabels();
      resetZoomLimit();
      return;
    }

    const lmFeatures = createLMFeatures(features);

    clusteredPinControlInstance.current?.updateFeatureLabels(lmFeatures, true);
    routingControlInstance.current?.clear();
    interactionControlInstance.current?.clearSelectedFeatureSource();

    if (features.length === 1) {
      // Immediately select the single feature and skip carousel
      handleFeatureSelect(features[0]);
      setShowKeyboardResults(false);
      setSearchTerm("");
      resetZoomLimit();
      return;
    }

    if (features.length > 1) {
      // Store search context for potential navigation back from POI view
      setPreviousSearchContext({
        searchTerm: searchValue,
        features: features,
        source: "keyboard",
      });

      setShowKeyboardResults(true);
      const bounds = getBoundingBox(features);
      const fitBoundsOptions: MapOptions["fitBoundsOptions"] = {
        bearing: map_primary.bearing,
        padding: 200,
      };

      mapInstance.current?.getMapboxMap().fitBounds(bounds, fitBoundsOptions);
    } else {
      setShowKeyboardResults(true);
    }

    resetZoomLimit();
  };

  const handleTouchMapRecentre = () => {
    handleOnTouch(InteractionEventTypes.RE_CENTER_MAP_TOUCH);
    setLocationBtnActive(true);
  };

  const handleFeatureSelect = (feature: Feature | null) => {
    if (feature) {
      isSessionResetting.current = false;
      setSelectedFeature(feature);
      setFeatureSelectionVersion((version) => version + 1);
      setShowKeyboardResults(false);
      // Keep previousSearchContext intact when selecting from search results
      // so we can navigate back to them
    }
  };

  const handleReturnToSearchResults = () => {
    if (previousSearchContext) {
      // Restore the previous search context
      setSelectedFeature(null);
      setCarouselFeatures([]);
      setShowKeyboardResults(false);
      setSubmittedSearchTerm(previousSearchContext.searchTerm);

      const lmFeatures = createLMFeatures(previousSearchContext.features);
      clusteredPinControlInstance.current?.updateFeatureLabels(
        lmFeatures,
        true,
      );

      if (previousSearchContext.features.length > 1) {
        const bounds = getBoundingBox(previousSearchContext.features);
        const fitBoundsOptions: MapOptions["fitBoundsOptions"] = {
          bearing: map_primary.bearing,
          padding: 200,
        };
        mapInstance.current?.getMapboxMap().fitBounds(bounds, fitBoundsOptions);
      }

      if (previousSearchContext.source === "keyboard") {
        setShowKeyboardResults(true);
        setUseSearchResultsCarousel(false);
      } else if (previousSearchContext.source === "quick_search") {
        setUseSearchResultsCarousel(true);
        const sortedContextFeatures = sortFeaturesByDistance(
          [location.longitude, location.latitude],
          previousSearchContext.features,
        );
        setCarouselFeatures(sortedContextFeatures);
      }

      resetZoomLimit();
    } else {
      // Default close behavior - clear everything
      setSelectedFeature(null);
      setCarouselFeatures([]);
      setShowKeyboardResults(false);
      setSubmittedSearchTerm("");
      setPreviousSearchContext(null);
      setUseSearchResultsCarousel(false);
      routingControlInstance.current?.clear();
      clusteredPinControlInstance.current?.clearFeatureLabels();
      interactionControlInstance.current?.clearSelectedFeatureSource();
    }
  };

  const handleMapUnavailable = useCallback(() => {
    mapInstance.current = null;
    floorControlInstance.current = null;
    positionControlInstance.current = null;
    clusteredPinControlInstance.current = null;
    routingControlInstance.current = null;
    interactionControlInstance.current = null;
    setMapIsReady(false);
  }, []);

  const handleMapReady = (map: LivingMap) => {
    mapInstance.current = map;
    floorControlInstance.current = map.getPluginById<FloorControl>(
      PLUGIN_IDS.FLOOR,
    );
    positionControlInstance.current = map.getPluginById<PositionPlugin>(
      PLUGIN_IDS.USER_LOCATION,
    );
    clusteredPinControlInstance.current = map.getPluginById<ClusteredPinPlugin>(
      PLUGIN_IDS.CLUSTERED_PIN,
    );
    routingControlInstance.current = map.getPluginById<RoutingPlugin>(
      PLUGIN_IDS.ROUTING,
    );
    interactionControlInstance.current = map.getPluginById<InteractionPlugin>(
      PLUGIN_IDS.INTERACTION,
    );

    resetZoomLimit();

    handleFeatureRouteLoad();
    handleQsSearchTagLoad();

    setMapIsReady(true);
    setMapReadyVersion((version) => version + 1);
  };

  const handleShowSearchResults = (
    searchTerm: string,
    features: Feature[],
    options?: { useSearchResultsCarousel?: boolean },
  ) => {
    setCarouselFeatures([]);
    setSelectedFeature(null);
    setSearchIsActive(false);
    setSubmittedSearchTerm(searchTerm);
    setShowKeyboardResults(false); // Hide keyboard results when showing POI results
    setUseSearchResultsCarousel(
      !!options?.useSearchResultsCarousel && features.length > 1,
    );

    if (options?.useSearchResultsCarousel && features.length > 1) {
      setPreviousSearchContext({
        searchTerm,
        features,
        source: "quick_search",
      });
    } else {
      setPreviousSearchContext(null);
    }

    clusteredPinControlInstance.current?.updateFeatureLabels(
      createLMFeatures(features),
      true,
    );
    routingControlInstance.current?.clear();

    if (features.length > 1) {
      const bounds = getBoundingBox(features);
      const fitBoundsOptions: MapOptions["fitBoundsOptions"] = {
        bearing: map_primary.bearing,
        padding: 200,
      };

      mapInstance.current?.getMapboxMap().fitBounds(bounds, fitBoundsOptions);

      // HACK: This will wait for the carousel to clear before updating the carousel features
      // as it is causing issues with the carousel animation
      setTimeout(() => {
        const sortedFeatures = sortFeaturesByDistance(
          [location.longitude, location.latitude],
          features,
        );
        setCarouselFeatures(sortedFeatures);
        if (!options?.useSearchResultsCarousel) {
          setSelectedFeature(sortedFeatures[0]);
        }
      });
    } else setSearchTerm("");

    resetZoomLimit();
  };

  const { handleFeatureRouteLoad } = useHandleFeatureRouteLoad({
    preloadedFeatures,
    clusteredPinControlInstance,
    onFeatureRouteLoad: resetTimeouts,
  });

  const { handleQsSearchTagLoad } = useHandleQsSearchTagLoad({
    combinedSearchTags,
    handleShowSearchResults,
    callback: () => {
      setHasInteracted(true);
      resetTimeouts();
    },
  });

  const handleSearchTagClick = (searchTag: SearchTagWithSource) => {
    handleOnTouch(InteractionEventTypes.SEARCH_TAG_TOUCH, {
      searchTagID: searchTag.id,
      title: parseLanguageObject(searchTag.name, i18n.language) || "",
    });

    handleShowSearchResults(
      parseLanguageObject(searchTag.name, i18n.language) || "",
      searchTag.features,
      { useSearchResultsCarousel: true },
    );
  };

  const handleSearchResultClick = (
    searchResultName: string,
    features: Feature[],
  ) => {
    // If ther are multiple features, we want to make sure that we get all the features with the same name from the
    // preloaded features as the search endpoint may not always return all the features under the same name because
    // there's a limit of 20 features getting returned from the api
    const allSearchFeatures =
      preloadedFeatures && features.length !== 1
        ? preloadedFeatures.filter(
            (feature) =>
              parseLanguageObject(
                feature.properties.popup_header,
                i18n.language,
              ) === searchResultName,
          )
        : features;

    handleOnTouch(InteractionEventTypes.SEARCH_RESULT_OPEN, {
      featureName: searchResultName,
    });

    handleShowSearchResults(searchResultName, allSearchFeatures);
  };

  const handleSearchControlClick = () => {
    setSearchIsActive(true);
    handleOnTouch(InteractionEventTypes.SEARCH_CONTROL_TOUCH);
  };

  const handleKeyboardClose = () => {
    setSearchIsActive(false);
    handleOnTouch(InteractionEventTypes.SEARCH_DIALOG_CLOSE);
  };

  const handleZoomClick = (zoomLevel: number) => {
    handleOnTouch(InteractionEventTypes.ZOOM_CONTROL_TOUCH);
    updateZoomLimit(zoomLevel);
  };

  const handleMoveControls = () => {
    handleOnTouch(
      moveDownPopups
        ? InteractionEventTypes.RAISE_CONTROLS
        : InteractionEventTypes.LOWER_CONTROLS,
    );
  };

  const handleShareToMobileClick = () => {
    setIsShareDialogOpen(true);
    handleOnTouch(InteractionEventTypes.SHARE_TO_MOBILE_KIOSK_VIEW);
  };

  const { stepFreeUnavailable } = useStepFreeRouting({
    featureId: selectedFeature?.id,
    selectionVersion: featureSelectionVersion,
    stepFree: isStepFreeRoute,
    requestRoute: handleRouteRequest,
    setStepFree: (value) => dispatch(setIsStepFreeRoute(value)),
    result: routeResult,
  });

  useEffect(() => {
    if (!selectedFeature) setStepFreeStatus(StepFreeStatus.INACTIVE);
  }, [selectedFeature]);

  useEffect(() => {
    if (!selectedFeature) return;

    handleTransportFeedsRequest();
  }, [selectedFeature, featureSelectionVersion, handleTransportFeedsRequest]);

  useEffect(() => {
    if (!isRoutingMode) return;

    routingControlInstance.current?.clear();

    if (displayedRoute && selectedFeature) {
      const { routeHasSteps } = renderRoute();

      if (routeHasSteps || displayedStepFree) {
        setStepFreeStatus(StepFreeStatus.SHOW);
      } else if (!displayedStepFree) {
        setStepFreeStatus(StepFreeStatus.HIDE);
      }

      handleUserLocationStyle();

      floorControlInstance.current?.setActiveFloor(floors[map_primary.floor]);

      handleOnTouch(InteractionEventTypes.ROUTE_SHOWN, {
        lm_id: selectedFeature?.properties?.lm_id,
        stepFree: displayedStepFree,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayedRoute,
    displayedStepFree,
    floors,
    handleUserLocationStyle,
    map_primary.floor,
    renderRoute,
    routeResult.isFetching,
    routeResult.isSuccess,
    isRoutingMode,
  ]);

  useEffect(() => {
    if (routeResult.isError) {
      checkOnlineMode();
      setStepFreeStatus(StepFreeStatus.HIDE);
      handleOnTouch(InteractionEventTypes.ROUTE_FAIL, {
        lm_id: selectedFeature?.properties?.lm_id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeResult.isError]);

  useEffect(() => {
    if (selectedFeature?.id && onlineMode)
      handleOnTouch(InteractionEventTypes.ASSET_DIALOG_OPEN, {
        lm_id: selectedFeature?.properties?.lm_id,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeature?.id]);

  useEffect(() => {
    if (!mapIsReady) return;

    positionControlInstance.current?.updateUserLocationStyle(
      userLocationStyle.colour,
      userLocationStyle.borderColour,
      userLocationStyle.displayPulse,
    );
  }, [
    mapIsReady,
    mapReadyVersion,
    userLocationStyle.colour,
    userLocationStyle.borderColour,
    userLocationStyle.displayPulse,
  ]);

  useEffect(() => {
    if (countdownTimeInSeconds === 0 || !isInactive) {
      setCountdownTimeInSeconds(noticeTimeout / 1000);

      return;
    }

    const intervalID = setInterval(() => {
      setCountdownTimeInSeconds((prevCountdown) => prevCountdown - 1);
    }, 1000);

    return () => {
      clearInterval(intervalID);
    };
  }, [
    checkOnlineMode,
    countdownTimeInSeconds,
    dispatch,
    isInactive,
    noticeTimeout,
  ]);

  useEffect(() => {
    if (mapControlsAlwaysLow || orientation === "landscape") {
      dispatch(setMoveDownPopups(true));
      dispatch(setControlsAlwaysLow(true));
    }
  }, [dispatch, mapControlsAlwaysLow, orientation]);

  useEffect(() => {
    document.body.classList.add(controlTheme);
    document.documentElement.classList.add(controlSize);
  }, [controlSize, controlTheme]);

  useEffect(() => {
    const onLanguageChanged = (language: string) => {
      const nextMapStylesheet = `${stylesheet}?lang=${language}`;

      if (nextMapStylesheet !== mapStylesheet) {
        handleMapUnavailable();
        setMapStylesheet(nextMapStylesheet);
      }

      setSelectedFeature(null);
      setCarouselFeatures([]);
      setPreviousSearchContext(null);
      setShowKeyboardResults(false);
      setSubmittedSearchTerm("");
      interactionControlInstance.current?.clearSelectedFeatureSource();
    };

    i18n.on("languageChanged", onLanguageChanged);

    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, [handleMapUnavailable, mapStylesheet, stylesheet]);

  const keyboardFeatures = keyboardSearchError
    ? []
    : (keyboardSearchResult?.data?.data ?? []);

  const sortedFeatures = addDistanceToFeatures(
    [location.longitude, location.latitude],
    keyboardFeatures,
  ) as Feature[];

  const showQuickSearchResultsCarousel =
    useSearchResultsCarousel &&
    carouselFeatures.length > 1 &&
    !selectedFeature &&
    !showKeyboardResults;

  const showPOICarousel =
    !useSearchResultsCarousel &&
    carouselFeatures.length > 1 &&
    !showKeyboardResults;

  const hasDisplayableRoute = Boolean(displayedRoute);
  const hideRouteStatusChip =
    stepFreeStatus === StepFreeStatus.SHOW && !stepFreeUnavailable;
  const routeStatusSuccess = !routeResult.isError && !stepFreeUnavailable;

  return (
    <div className={styles.container}>
      {top_bar && (
        <Header
          lang={top_bar.lang!}
          theme={top_bar.theme}
          icon={top_bar.icon}
          time={server.time}
          timezone={location.timezone}
        />
      )}
      {iframe && (
        <IFrame
          dataQA="iframe-container"
          backgroundColor={
            iframe.theme.background_color ? iframe.theme.background_color : ""
          }
          height={iframe.height}
          position={iframe.position}
          url={iframe.url}
        />
      )}
      <div className={styles.primaryMap}>
        <UIProvider screenOrientation={orientation}>
          <Map
            mapID={screen_id}
            bearing={map_primary.bearing}
            zoom={map_primary.zoom.init}
            maxZoom={map_primary.zoom.max}
            minZoom={map_primary.zoom.min}
            center={map_primary.center}
            extent={map_primary.extent}
            mapStyle={mapStylesheet}
            floor={map_primary.floor}
            floors={floors}
            accessToken={mapboxApiKey}
            features={preloadedFeatures}
            youMarker={map_primary.you_marker}
            controlTheme={controlTheme}
            controlSize={controlSize}
            interactive={!!map_primary.interaction.enabled}
            enableTouchZoom={primaryControls.show.zoom_controls}
            onTouch={handleOnTouch}
            onZoom={updateZoomLimit}
            onMapUnavailable={handleMapUnavailable}
            onMapReady={handleMapReady}
            onMapDrag={() => {
              setHasInteracted(true);
              setLocationBtnActive(false);
            }}
            onMapInteract={() => {
              isSessionResetting.current = false;
              resetTimeouts();
            }}
            featureHorizonHeight={
              primaryControls.theme.feature_horizon_height
                ? primaryControls.theme.feature_horizon_height
                : fullHD
                  ? DEFAULT_FEATURE_HORIZON_HEIGHT
                  : DEFAULT_FEATURE_HORIZON_HEIGHT * 2
            }
            onFeatureSelect={(feature) =>
              handleFeatureSelect(feature as Feature)
            }
            initZoom={map_primary.zoom.init}
            onMoveControls={handleMoveControls}
            // Pass the height of the IFrame to the map only when the IFrame is at the bottom because we don't need to adjust the floating modals when the IFrame is at the top
            iframeHeight={iframe?.position === "bottom" ? iframe?.height : 0}
            clearCarouselFeatures={() => setCarouselFeatures([])}
            distanceUnit={routing?.display_unit_system ?? "metric"}
          >
            {preloadedFeatures &&
              transitions(
                (transitionStyles, show) =>
                  show && (
                    <MoveDownView
                      className={classNames(styles.searchWrapper, {
                        [styles.accessibleHeight]: moveDownPopups,
                      })}
                      onBgClick={handleKeyboardClose}
                    >
                      <animated.div
                        style={transitionStyles}
                        className={styles.animatedWrapper}
                      >
                        <div className={styles.searchHeightPadder}>
                          {isOfflineSearchEnabled &&
                            !isFetchingSearchResults &&
                            debouncedSearchTerm.length >= MIN_CHARS && (
                              <SearchResults
                                dataQA="search-results"
                                className={styles.searchResultsContainer}
                                results={searchResults}
                                isTitleSearch={isTitleSearch}
                                searchTerm={searchTerm.trim()}
                                onResultClick={(features) => {
                                  const popup_header = parseLanguageObject(
                                    features[0].properties.popup_header,
                                    i18n.language,
                                  );

                                  if (popup_header)
                                    handleSearchResultClick(
                                      popup_header,
                                      features,
                                    );
                                }}
                                onScroll={throttle(resetTimeouts)}
                                enableImages={enable_images}
                                floors={floors}
                                defaultFloorId={floors[map_primary.floor].id}
                              />
                            )}
                        </div>
                        <Keyboard
                          searchTerm={submittedSearchTerm || ""}
                          project={kioskTitle}
                          dataQA="search-keyboard"
                          onClose={handleKeyboardClose}
                          onChange={handleKeyboardOnChange}
                          enableInput={searchInputActive}
                          onSearch={handleKeyboardSearch}
                          languageKey={i18n.language}
                          isLatinLanguage={isLatinLanguage}
                          showSearchKey={onlineMode}
                        />
                      </animated.div>
                    </MoveDownView>
                  ),
              )}

            {primaryControls.show.re_center_map && (
              <CentreControl
                isInactive={isInactive}
                onTouch={handleOnTouch}
                countdownTimeInSeconds={countdownTimeInSeconds}
              />
            )}
            {primaryControls.show.north_marker && (
              <div className={styles.primaryCompassContainer}>
                <Compass bearing={map_primary.bearing} />
              </div>
            )}
            <ControlsWrapper
              footerHeight={footerHeight}
              mapControlMarginBottom={mapControlMarginBottom}
              leftMapControlsContent={
                primaryControls.show.zoom_controls ||
                primaryControls.show.pan_control ? (
                  <div>
                    {primaryControls.show.pan_control && (
                      <div className={styles.controlRightMargin}>
                        <PanControl
                          onTouch={handleOnTouch}
                          hasPanned={hasInteracted}
                          setHasPanned={setHasInteracted}
                        />
                      </div>
                    )}
                    {primaryControls.show.zoom_controls && (
                      <ZoomControl
                        className={styles.zoomControlContainer}
                        minZoomReached={isZoomAtLimit.min}
                        maxZoomReached={isZoomAtLimit.max}
                        onZoomClick={handleZoomClick}
                      />
                    )}
                  </div>
                ) : null
              }
              rightMapControlsContent={
                primaryControls.show.level_selector ? (
                  <>
                    {primaryControls.show.level_selector && (
                      <FloorSelector
                        dataQA="floor-container"
                        floors={floors}
                        youAreHereFloor={map_primary.you_marker.floor}
                        onTouch={handleOnTouch}
                        poiFloorId={selectedFeature?.properties?.poi_floor_id}
                        activeFloor={
                          floorControlInstance.current?.getActiveFloor() ||
                          map_primary.floor
                        }
                      />
                    )}
                    {primaryControls.show.re_center_map && (
                      <div className={styles.controlTopMargin}>
                        <MapControlLocationButton
                          dataQA="location-button"
                          onClick={handleTouchMapRecentre}
                          size="large"
                          status={
                            !hasInteracted || locationBtnActive
                              ? LocationStatus.FOUND
                              : LocationStatus.INACTIVE
                          }
                        />
                      </div>
                    )}
                  </>
                ) : null
              }
              searchControlsContent={
                <>
                  {isRoutingMode && (
                    <div
                      className={classNames(styles.toggleContainer, {
                        [styles.hidden]:
                          !selectedFeature ||
                          (onlineMode &&
                            stepFreeStatus === StepFreeStatus.INACTIVE),
                      })}
                    >
                      <ToggleSwitch
                        className={classNames(styles.toggle, {
                          [styles.hidden]:
                            !onlineMode ||
                            !hasDisplayableRoute ||
                            stepFreeStatus === StepFreeStatus.HIDE,
                        })}
                        dataQA="step-free-toggle"
                        label={t("base_with_header.avoid_stairs_toggle_text")}
                        isToggled={isStepFreeRoute}
                        onToggle={() => {
                          routingControlInstance.current?.clear();
                          dispatch(setIsStepFreeRoute(!isStepFreeRoute));
                          handleOnTouch(
                            isStepFreeRoute
                              ? InteractionEventTypes.AVOID_STAIRS_OFF
                              : InteractionEventTypes.AVOID_STAIRS_ON,
                          );
                        }}
                      />
                      <StepFreeChip
                        dataQA="step-free-chip"
                        className={classNames({
                          [styles.hidden]: onlineMode && hideRouteStatusChip,
                        })}
                        success={onlineMode && routeStatusSuccess}
                        message={
                          !onlineMode
                            ? t("step_free_chip.offline_route", {
                                defaultValue:
                                  "Unable to generate route whilst offline",
                              })
                            : undefined
                        }
                      />
                    </div>
                  )}
                  {keyboardSearchResult.isFetching && (
                    <div className={styles.floatingSearchButton}>
                      <FloatingButton
                        text="Searching"
                        // eslint-disable-next-line react/style-prop-object
                        style="filled"
                        type="primary"
                        size="regular"
                        dataQA="searching-floating-button"
                        icon="search"
                      />
                    </div>
                  )}
                  {preloadedFeatures && primaryControls.show.search && (
                    <div className={styles.searchContainer}>
                      <SearchControl
                        dataQA="search"
                        buttonStyle="smallSquare"
                        onClick={handleSearchControlClick}
                        project={kioskTitle}
                        searchTerm={submittedSearchTerm}
                        hasInteracted={searchIsActive}
                        multipleResults={
                          carouselFeatures.length > 1 ||
                          previousSearchContext?.features.length !== null
                        }
                        onClearClick={() => {
                          setSubmittedSearchTerm("");
                          setSelectedFeature(null);
                          setCarouselFeatures([]);
                          setShowKeyboardResults(false);
                          setPreviousSearchContext(null);
                          setUseSearchResultsCarousel(false);
                          routingControlInstance.current?.clear();
                          clusteredPinControlInstance.current?.clearFeatureLabels();
                          interactionControlInstance.current?.clearSelectedFeatureSource();
                        }}
                      />
                    </div>
                  )}
                  {combinedSearchTags.length > 0 &&
                    !selectedFeature &&
                    !carouselFeatures.length &&
                    !showKeyboardResults && (
                      <div
                        className={classNames(styles.searchTagsContainer, {
                          [styles.accessibleHeight]: moveDownPopups,
                        })}
                      >
                        <SearchTags
                          dataQA="search-tags"
                          searchTags={combinedSearchTags}
                          onSearchTagClick={handleSearchTagClick}
                        />
                      </div>
                    )}
                  {(carouselFeatures.length > 1 ||
                    selectedFeature ||
                    showKeyboardResults) && (
                    <div
                      className={classNames(styles.poiContainer, {
                        [styles.accessibleHeight]: moveDownPopups,
                      })}
                    >
                      {showKeyboardResults ? (
                        <div
                          className={classNames(styles.carousel, {
                            [styles.accessibleHeight]: moveDownPopups,
                          })}
                        >
                          <SearchResultsCarousel
                            features={sortedFeatures}
                            floors={floors}
                            searchTags={combinedSearchTags}
                            onSearchTagClick={handleSearchTagClick}
                            onSelect={(featureWithDistance) => {
                              // POICarousel passes FeatureWithDistance, which extends Feature
                              handleFeatureSelect(
                                featureWithDistance as Feature,
                              );
                              clusteredPinControlInstance.current?.updateSearchFeatureLabel(
                                featureWithDistance,
                              );
                            }}
                            resetTimeouts={resetTimeouts}
                            isError={
                              keyboardSearchError ||
                              keyboardSearchResult.isError
                            }
                          />
                        </div>
                      ) : showQuickSearchResultsCarousel ? (
                        <div
                          className={classNames(styles.carousel, {
                            [styles.accessibleHeight]: moveDownPopups,
                          })}
                        >
                          <SearchResultsCarousel
                            features={carouselFeatures}
                            floors={floors}
                            searchTags={combinedSearchTags}
                            onSearchTagClick={handleSearchTagClick}
                            onSelect={(featureWithDistance) => {
                              handleFeatureSelect(
                                featureWithDistance as Feature,
                              );
                              clusteredPinControlInstance.current?.updateSearchFeatureLabel(
                                featureWithDistance,
                              );
                            }}
                            resetTimeouts={resetTimeouts}
                          />
                        </div>
                      ) : showPOICarousel ? (
                        <POICarousel
                          dataQA="search-results-carousel"
                          features={carouselFeatures}
                          className={classNames(styles.carousel, {
                            [styles.accessibleHeight]: moveDownPopups,
                          })}
                          floors={floors}
                          defaultFloorId={floors[map_primary.floor].id}
                          selectedFeature={selectedFeature}
                          onSlideChange={(feature) => {
                            clusteredPinControlInstance.current?.updateSearchFeatureLabel(
                              feature,
                            );
                          }}
                          transportFeeds={transportFeedsResult.currentData}
                          transportFeedsApi={transportFeedsResult.originalArgs}
                          transportFeedsIsFetching={
                            transportFeedsResult.isFetching
                          }
                          transportFeedsIsError={transportFeedsResult.isError}
                        />
                      ) : (
                        <SheetContainer
                          className={classNames(styles.singlePoi, {
                            [styles.accessibleHeight]: moveDownPopups,
                          })}
                          defaultFloorId={floors[map_primary.floor].id}
                          floors={floors}
                          selectedFeature={selectedFeature}
                          onClose={() => {
                            clusteredPinControlInstance.current?.clearSelectedFeature();
                            interactionControlInstance.current?.clearSelectedFeatureSource();
                            handleOnTouch(
                              InteractionEventTypes.ASSET_DIALOG_CLOSE,
                            );
                            handleReturnToSearchResults();
                          }}
                          {...(!routeResult.isFetching && {
                            kilometres: displayedRoute?.routeMetadata.reduce(
                              (acc, curr) => acc + curr.totalLength,
                              0,
                            ),
                          })}
                          transportFeeds={transportFeedsResult.currentData}
                          transportFeedsApi={transportFeedsResult.originalArgs}
                          transportFeedsIsFetching={
                            transportFeedsResult.isFetching
                          }
                          transportFeedsIsError={transportFeedsResult.isError}
                          resetTimeouts={resetTimeouts}
                        />
                      )}
                    </div>
                  )}
                </>
              }
            />
            <Footer
              controlsAlwaysLow={controlsAlwaysLow}
              footerRef={footerRef}
              shareToMobileComponent={
                <ShareToMobile
                  shortlinkUrl={buildKioskShortlink()}
                  isDialogOpen={isShareDialogOpen}
                  onButtonClick={handleShareToMobileClick}
                  onClose={() => {
                    setIsShareDialogOpen(false);
                    resetTimeouts();
                  }}
                />
              }
              langSelectComponent={
                languages.available.length > 1 ? (
                  <LanguageSelect
                    languages={languages.available}
                    onButtonClick={() => {
                      setIsLanguageDialogOpen(true);
                      resetTimeouts();
                    }}
                    isDialogOpen={isLanguageDialogOpen}
                    onClose={() => {
                      setIsLanguageDialogOpen(false);
                      resetTimeouts();
                    }}
                    onLanguageChange={async (id) => {
                      resetTimeouts();
                      if (id === i18n.language) return;

                      const response = await triggerHealthCheck();
                      if (!response.isError) {
                        i18n.changeLanguage(id);

                        handleOnTouch(InteractionEventTypes.LANGUAGE_CHANGE, {
                          language: id,
                        });

                        const direction = data.languages.available.find(
                          (lang) => lang.id === id,
                        )?.orientation;
                        if (direction)
                          document.documentElement.setAttribute(
                            "dir",
                            direction,
                          );
                      } else setIsLanguageChangeErrorModalOpen(true);
                    }}
                  />
                ) : null
              }
            />
            {!isDebugMode && (
              <MessagingProtocol
                display={display}
                timezone={location.timezone}
                uptime={uptime}
                onTick={checkOnlineMode}
              />
            )}
            {isLanguageChangeErrorModalOpen && (
              <LanguageChangeErrorModal
                onClick={() => {
                  setIsLanguageChangeErrorModalOpen(false);
                  resetTimeouts();
                }}
              />
            )}
          </Map>
        </UIProvider>
      </div>
    </div>
  );
};

export default BaseWithHeader;
